import { useContext } from "react"
import { useNavigate } from "react-router-dom"
import { casesService } from "@/features/cases/services/cases.service"
import { ThreadContext } from "@/providers/ThreadProvider"
import type { Thread } from "@/types"
import { saveSimulatorHandoff, type PreparedOiaInput, type PreparedOioInput } from "@/features/drafting/simulatorHandoff"

type SimulatorSubmission =
  | [service: "oio", prepared: PreparedOioInput, metadata: Record<string, unknown>]
  | [service: "oia", prepared: PreparedOiaInput, metadata: Record<string, unknown>]

/** Creates the case only after the caller has finished all authenticated uploads. */
export function useSimulatorSubmit(userId: string, isCurrentUser: () => boolean) {
  const threadContext = useContext(ThreadContext)
  const navigate = useNavigate()
  function submit(service: "oio", prepared: PreparedOioInput, metadata: Record<string, unknown>): Promise<void>
  function submit(service: "oia", prepared: PreparedOiaInput, metadata: Record<string, unknown>): Promise<void>
  async function submit(...args: SimulatorSubmission): Promise<void> {
    const [service, prepared, metadata] = args
    if (!userId || !isCurrentUser()) throw new Error("Your account changed. Reopen the simulator to continue.")
    const filename = service === "oio"
      ? (prepared as PreparedOioInput).scn.filename
      : (prepared as PreparedOiaInput).documents[0]?.filename
    const title = filename ? filename.replace(/\.[^.]+$/, "") : service === "oio" ? "New OIO case" : "New OIA case"
    const updated = await casesService.create(title, service, userId, metadata)
    if (!isCurrentUser()) throw new Error("Your account changed. Reopen the simulator to continue.")
    const submissionId = globalThis.crypto?.randomUUID?.() ?? `sim-${Date.now()}`
    if (service === "oio") {
      saveSimulatorHandoff({ userId, caseId: updated.id, service, submissionId, prepared })
    } else {
      saveSimulatorHandoff({ userId, caseId: updated.id, service, submissionId, prepared })
    }
    threadContext?.setActiveThread(updated as Thread)
    navigate("/drafting")
  }
  return submit
}
